import { useMemo } from 'react';

export type IamEffect = 'Allow' | 'Deny';
export type IamResourceScope = 'OWN' | 'ALL';

export interface IamPolicyStatement {
  effect: IamEffect;
  actions: string[];
  resourceScope: IamResourceScope;
}

export interface PermissionAction {
  code: string;
  /** 服务分组的稳定值（来源于 group_value）。 */
  service: string;
  /** 服务分组的展示标签（来源于 group）。 */
  serviceLabel?: string;
  serviceName?: string;
  group: '读操作' | '写操作' | '列表操作';
  name: string;
  description?: string;
}

export interface PolicyPermissionRuleView {
  effect: IamEffect;
  resourceScope: IamResourceScope;
  sources?: PolicyPermissionSourceView[];
}

export interface PolicyPermissionSourceView {
  key: string;
  label: string;
}

export interface PolicyPermissionItem {
  code: string;
  name: string;
  groupLabel: string;
  groupValue: string;
  rules: PolicyPermissionRuleView[];
}

interface PermissionActionSource {
  code: string;
  display_name: string;
  group: string;
  group_value?: string;
  service_name?: string;
  decision?: {
    allowed: boolean;
    range: string;
  };
  rules?: readonly {
    effect: string;
    range: string;
  }[];
  source?: readonly {
    grant_id: number;
    source_type: 'direct' | 'group_inherited';
    effect: string;
    range: string;
    policy_id: string;
    policy_name: string;
    identity: string;
    identity_name: string;
  }[];
}

interface UsePolicyPermissionItemsOptions {
  /** 策略或授权详情接口返回的原生权限汇总。 */
  actions?: readonly PermissionActionSource[];
  /** 兼容尚未迁移到详情接口的调用方。 */
  statements?: readonly IamPolicyStatement[];
  actionCatalog?: readonly PermissionAction[];
}

function getRuleEffect(effect: string): IamEffect {
  return effect.toLowerCase() === 'deny' ? 'Deny' : 'Allow';
}

function getRuleScope(range: string): IamResourceScope {
  return range.toLowerCase() === 'self' ? 'OWN' : 'ALL';
}

/**
 * 统一策略与授权权限点的展示模型，并在输入变化时才重新计算。
 * 原生汇总按同一条 rules/source 的 effect/range 配对，二者都缺失时才回退 decision。
 */
export function usePolicyPermissionItems({
  actions,
  statements,
  actionCatalog,
}: UsePolicyPermissionItemsOptions): PolicyPermissionItem[] {
  return useMemo(() => {
    if (actions) {
      return actions.map((action) => {
        const fallbackGroup =
          action.code === '*' ? '全部服务' : action.code.split(':')[0] || '未分组';
        const groupValue =
          action.group_value || action.service_name || action.group || fallbackGroup;
        const rulesByKey = new Map<string, PolicyPermissionRuleView>();
        const addRule = (
          rawEffect: string,
          rawRange: string,
          source?: PolicyPermissionSourceView
        ) => {
          const effect = getRuleEffect(rawEffect);
          const resourceScope = getRuleScope(rawRange);
          const key = `${effect}:${resourceScope}`;
          const rule = rulesByKey.get(key) ?? { effect, resourceScope, sources: [] };
          if (source && !rule.sources?.some((item) => item.key === source.key)) {
            rule.sources?.push(source);
          }
          rulesByKey.set(key, rule);
        };

        action.rules?.forEach((rule) => addRule(rule.effect, rule.range));
        action.source?.forEach((source) => {
          const sourceName =
            source.source_type === 'group_inherited'
              ? source.identity_name || source.identity
              : source.policy_name || source.policy_id;
          addRule(source.effect, source.range, {
            key: `${source.source_type}:${source.grant_id}:${source.policy_id}:${source.identity}`,
            label: `${source.source_type === 'group_inherited' ? '用户组继承' : '直接授权'} · ${sourceName}`,
          });
        });
        if (!rulesByKey.size && action.decision) {
          addRule(action.decision.allowed ? 'Allow' : 'Deny', action.decision.range);
        }

        return {
          code: action.code,
          name: action.display_name || action.code,
          groupLabel: action.group || action.service_name || groupValue,
          groupValue,
          rules: [...rulesByKey.values()],
        };
      });
    }

    const catalogByCode = new Map((actionCatalog ?? []).map((action) => [action.code, action]));
    const itemsByCode = new Map<string, PolicyPermissionItem>();

    (statements ?? []).forEach((statement) => {
      statement.actions.forEach((code) => {
        const action = catalogByCode.get(code);
        const fallbackGroup = code === '*' ? '全部服务' : code.split(':')[0] || '未分组';
        const groupValue = action?.service || fallbackGroup;
        const groupLabel = action?.serviceLabel || action?.serviceName || groupValue;
        const item = itemsByCode.get(code) ?? {
          code,
          name: action?.name || (code === '*' ? '全部权限点' : code),
          groupLabel,
          groupValue,
          rules: [],
        };
        if (
          !item.rules.some(
            (rule) =>
              rule.effect === statement.effect && rule.resourceScope === statement.resourceScope
          )
        ) {
          item.rules.push({
            effect: statement.effect,
            resourceScope: statement.resourceScope,
          });
        }
        itemsByCode.set(code, item);
      });
    });

    return [...itemsByCode.values()];
  }, [actionCatalog, actions, statements]);
}
